import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, Chip,
    TextField, CircularProgress, Alert, Switch, FormControlLabel,
    Tooltip, IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ArticleIcon from '@mui/icons-material/Article';
import api from '../services/api';

const Container = styled.div`
  max-width: 1400px;
  margin: 40px auto;
  padding: 0 20px;
`;

const stickyActionCellSx = {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    backgroundColor: 'background.paper',
    boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.08)',
    minWidth: 170
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const AdminPortal = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteUsername, setInviteUsername] = useState('');
    const [feedback, setFeedback] = useState({ msg: '', type: '' });
    const [systemStatus, setSystemStatus] = useState({});
    const adminUser = JSON.parse(localStorage.getItem('user'));

    const fetchUsers = React.useCallback(async () => {
        try {
            const data = await api.adminListUsers(adminUser.id);
            setUsers(data);
        } catch (error) {
            console.error(error);
            setFeedback({ msg: 'Failed to load users: ' + error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [adminUser.id]);

    const fetchSystemStatus = React.useCallback(async () => {
        try {
            const data = await api.getSystemStatus(adminUser.id);
            setSystemStatus(data);
        } catch (error) {
            console.error('Status fetch failed', error);
        }
    }, [adminUser.id]);

    useEffect(() => {
        fetchUsers();
        fetchSystemStatus();
    }, [fetchUsers, fetchSystemStatus]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteUsername) return;

        try {
            const res = await api.adminInviteUser(adminUser.id, inviteUsername);
            setFeedback({ msg: res.message, type: 'success' });
            setInviteUsername('');
            fetchUsers(); // Refresh list to show potential updates
        } catch (error) {
            setFeedback({ msg: error.message, type: 'error' });
        }
    };

    const handleToggleAccess = async (targetId, currentStatus) => {
        try {
            await api.adminToggleAccess(adminUser.id, targetId, !currentStatus);
            setUsers(prev => prev.map(u =>
                u.id === targetId ? { ...u, is_beta_allowed: !currentStatus ? 1 : 0 } : u
            ));
        } catch (error) {
            setFeedback({ msg: 'Update failed: ' + error.message, type: 'error' });
        }
    };

    const handleToggleAutoApprove = async () => {
        const manualApprovalRequired = systemStatus.auto_approve_beta?.value !== 'true';
        const nextManualApprovalRequired = !manualApprovalRequired;
        const newValue = nextManualApprovalRequired ? 'false' : 'true';
        try {
            await api.adminUpdateSystemStatus(adminUser.id, 'auto_approve_beta', newValue);
            setSystemStatus(prev => ({
                ...prev,
                auto_approve_beta: { value: newValue, updated_at: new Date().toISOString() }
            }));
            setFeedback({
                msg: nextManualApprovalRequired
                    ? 'New accounts now require manual approval.'
                    : 'New accounts will now be approved automatically.',
                type: 'success'
            });
        } catch (error) {
            setFeedback({ msg: 'Update failed: ' + error.message, type: 'error' });
        }
    };

    const handleFixUrls = async () => {
        try {
            const res = await api.fixKriticProfileUrls();
            setFeedback({ msg: `Fixed ${res.totalFixed} URLs`, type: 'success' });
        } catch (error) {
            setFeedback({ msg: 'Fix failed: ' + error.message, type: 'error' });
        }
    };

    const handleTriggerScraper = async () => {
        try {
            const res = await api.adminTriggerScraper(adminUser.id);
            setFeedback({ msg: res.message, type: 'success' });
            // Refresh status after a few seconds
            setTimeout(fetchSystemStatus, 3000);
        } catch (error) {
            setFeedback({ msg: 'Trigger failed: ' + error.message, type: 'error' });
        }
    };

    const handleTriggerTMDBSync = async () => {
        try {
            const res = await api.adminTriggerTMDBSync(adminUser.id, 2);
            setFeedback({ msg: res.message, type: 'success' });
            // Refresh status after a few seconds
            setTimeout(fetchSystemStatus, 3000);
        } catch (error) {
            setFeedback({ msg: 'Trigger failed: ' + error.message, type: 'error' });
        }
    };

    const handleDeleteUser = async (targetId, username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"? This will permanently remove all their ratings, watchlist items, and data. This action cannot be undone.`)) {
            return;
        }

        try {
            await api.adminDeleteUser(adminUser.id, targetId);
            setFeedback({ msg: `User "${username}" deleted successfully.`, type: 'success' });
            fetchUsers();
        } catch (error) {
            setFeedback({ msg: 'Deletion failed: ' + error.message, type: 'error' });
        }
    };

    if (!adminUser?.isAdmin) {
        return <Container><Alert severity="error">Access Denied</Alert></Container>;
    }

    return (
        <Container>
            <Header>
                <Typography variant="h4" sx={{ fontFamily: '"Carter One", cursive' }}>Admin Portal</Typography>
            </Header>

            {feedback.msg && (
                <Alert severity={feedback.type} onClose={() => setFeedback({ msg: '', type: '' })} sx={{ mb: 3 }}>
                    {feedback.msg}
                </Alert>
            )}

            <Section>
                <Typography variant="h6" gutterBottom>Deployment Info</Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">Frontend (Pages)</Typography>
                        <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">
                                Version: {process.env.REACT_APP_GIT_SHA?.substring(0, 7) || 'unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Branch: {process.env.REACT_APP_GIT_BRANCH || 'unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Built: {process.env.REACT_APP_BUILD_TIME ? new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString() : 'unknown'}
                            </Typography>
                        </Box>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">Backend (Workers)</Typography>
                        <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">
                                Version: {systemStatus.deployment_info?.backend?.version?.substring(0, 7) || 'unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Branch: {systemStatus.deployment_info?.backend?.branch || 'unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Deployed: {systemStatus.deployment_info?.backend?.deployed_at ? new Date(systemStatus.deployment_info.backend.deployed_at).toLocaleString() : 'unknown'}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>System Status</Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" color="textSecondary">TMDB Movies Sync</Typography>
                            <Tooltip title="View Logs">
                                <IconButton
                                    size="small"
                                    onClick={() => window.open('https://dash.cloudflare.com', '_blank')}
                                >
                                    <ArticleIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box mt={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight="bold">
                                    {systemStatus.movies_update ? JSON.parse(systemStatus.movies_update.value).status : 'Unknown'}
                                </Typography>
                                {systemStatus.movies_update && JSON.parse(systemStatus.movies_update.value).status === 'running' && (
                                    <Chip label="Running" color="primary" size="small" />
                                )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                                {systemStatus.movies_update ? new Date(systemStatus.movies_update.updated_at).toLocaleString() : '-'}
                            </Typography>
                            {systemStatus.movies_update && JSON.parse(systemStatus.movies_update.value).added !== undefined && (
                                <Typography variant="caption" display="block">
                                    Added: {JSON.parse(systemStatus.movies_update.value).added} movies
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleTriggerTMDBSync}
                                >
                                    Trigger Sync
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" color="textSecondary">RT Kritics Scraper</Typography>
                            <Tooltip title="View Logs">
                                <IconButton
                                    size="small"
                                    onClick={() => window.open('https://dash.cloudflare.com', '_blank')}
                                >
                                    <ArticleIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box mt={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight="bold">
                                    {systemStatus.scrapers_update ? JSON.parse(systemStatus.scrapers_update.value).status : 'Unknown'}
                                </Typography>
                                {systemStatus.scrapers_update && JSON.parse(systemStatus.scrapers_update.value).status === 'running' && (
                                    <Chip label="Running" color="primary" size="small" />
                                )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                                {systemStatus.scrapers_update ? new Date(systemStatus.scrapers_update.updated_at).toLocaleString() : '-'}
                            </Typography>
                            {systemStatus.scrapers_update && JSON.parse(systemStatus.scrapers_update.value).letter !== undefined && (
                                <Typography variant="caption" display="block">
                                    Letter: {JSON.parse(systemStatus.scrapers_update.value).letter.toUpperCase()}
                                    {JSON.parse(systemStatus.scrapers_update.value).count !== undefined &&
                                        ` (${JSON.parse(systemStatus.scrapers_update.value).count} kritics)`}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleTriggerScraper}
                                >
                                    Trigger Scrape
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">Beta Access</Typography>
                        <Box mt={1}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={systemStatus.auto_approve_beta?.value !== 'true'}
                                        onChange={handleToggleAutoApprove}
                                        color="primary"
                                    />
                                }
                                label="Require manual approval"
                            />
                            <Typography variant="caption" color="textSecondary" display="block">
                                {systemStatus.auto_approve_beta?.value === 'true'
                                    ? 'Off — new accounts are approved automatically.'
                                    : 'On — an admin must approve each new account.'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                                {systemStatus.auto_approve_beta?.updated_at ? `Last updated: ${new Date(systemStatus.auto_approve_beta.updated_at).toLocaleString()}` : 'Never updated'}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>Invite Beta User</Typography>
                <Box component="form" onSubmit={handleInvite} sx={{ display: 'flex', gap: 2, maxWidth: 500 }}>
                    <TextField
                        size="small"
                        label="Username"
                        fullWidth
                        value={inviteUsername}
                        onChange={(e) => setInviteUsername(e.target.value)}
                        placeholder="Enter username to allow"
                    />
                    <Button variant="contained" type="submit">Invite</Button>
                </Box>
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>Maintenance</Typography>
                <Button variant="outlined" onClick={handleFixUrls}>Fix Kritic Profile URLs</Button>
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>User Management</Typography>
                {loading ? <CircularProgress /> : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Last Login</TableCell>
                                    <TableCell align="center">Ranked</TableCell>
                                    <TableCell align="center">Watchlist</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Admin</TableCell>
                                    <TableCell sx={{ ...stickyActionCellSx, zIndex: 3 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(user => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{user.username}</TableCell>
                                        <TableCell>{user.email || '-'}</TableCell>
                                        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={user.ranked_count || 0} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={user.watchlist_count || 0} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.is_beta_allowed ? "Allowed" : "Pending"}
                                                color={user.is_beta_allowed ? "success" : "warning"}
                                                size="small"
                                                icon={user.is_beta_allowed ? <CheckCircleIcon /> : <BlockIcon />}
                                            />
                                        </TableCell>
                                        <TableCell>{user.is_admin ? "Yes" : "No"}</TableCell>
                                        <TableCell sx={stickyActionCellSx}>
                                            {!user.is_admin && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color={user.is_beta_allowed ? "warning" : "success"}
                                                        onClick={() => handleToggleAccess(user.id, user.is_beta_allowed)}
                                                    >
                                                        {user.is_beta_allowed ? "Revoke" : "Grant"}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => handleDeleteUser(user.id, user.username)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Section>
        </Container>
    );
};

export default AdminPortal;
